import { createTheme } from '@mui/material/styles';

// Type declarations
declare module '@mui/material/styles' {
  interface Palette {
    custom: {
      sidebar: string;
      background: string;
    };
  }
  
  interface PaletteOptions {
    custom?: {
      sidebar?: string;
      background?: string;
    };
  }

  interface TypeText {
    tertiary: string;
  }

  // Group all component style overrides together
  interface Components {
    MuiAppContainer?: {
      styleOverrides?: {
        root?: React.CSSProperties;
      };
    };
    MuiButtonControls?: {
      styleOverrides?: {
        root?: React.CSSProperties;
      };
    };
    MuiSidebarSection?: {
      styleOverrides?: {
        root?: React.CSSProperties;
      };
    };
    MuiSidebarContent?: {
      styleOverrides?: {
        root?: React.CSSProperties;
      };
    };
    MuiStyledDrawer?: {
      styleOverrides?: {
        root?: React.CSSProperties;
      };
    };
    MuiSlidingSidebar?: {
      styleOverrides?: {
        root?: React.CSSProperties;
      };
    };
    MuiSidebarEditForm?: {
      styleOverrides?: {
        root?: React.CSSProperties;
        section?: React.CSSProperties;
        field?: {
          marginBottom: string;
          [key: string]: any;
        };
      };
    };
  }
}

// Theme configuration
const theme = createTheme({
  // Color Palette
  palette: {
    primary: {
      main: '#6B20F5', // Vibrant purple
    },
    secondary: {
      main: '#816EB4', // Muted lavender
    },
    text: {
      primary: '#020239',   // Dark navy blue
      secondary: '#8678A7', // Light grayish-blue
      tertiary: '#E1E2EF', // Very light grayish-blue
    },
    background: {
      default: '#FAF8FF',
      paper: '#FAF8FF',
    },
    custom: {
      sidebar: '#150218',   // Dark blue
      background: '#05204A', // Deep navy blue
    },
  },

  // Typography
  typography: {
    fontFamily: 'Avenir, Helvetica, Arial, sans-serif',
    fontSize: 13,
    h5: { fontSize: '1.2rem' },
    h6: { fontSize: '1rem' },
    body1: { fontSize: '0.9rem' },
    body2: { fontSize: '0.8rem' },  // Our target size for DynamicTable and DynamicForm
  },

  // Component Styling
  components: {
    // Global Styles
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#05204A',
          color: '#020239',
          fontFamily: 'Avenir, Helvetica, Arial, sans-serif',
        },
        'h1, h2, h3, h4, h5, h6, p, span, div': {
          fontFamily: 'Avenir, Helvetica, Arial, sans-serif',
        },
      },
    },

    // Basic Components
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#02020A',
          color: '#E1E2EF',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#FAF8FF',
        },
      },
    },

    // Layout Components
    MuiAppContainer: {
      styleOverrides: {
        root: {
          display: 'flex',
          backgroundColor: '#F1F0FF',
          minHeight: '100vh',
          minWidth: '100vw',
          paddingRight: '35px !important',
        },
      },
    },

    // Sidebar Components
    MuiSidebarContent: {
      styleOverrides: {
        root: {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }
      },
    },
    MuiSidebarSection: {
      styleOverrides: {
        root: {
          padding: '8px',
        },
      },
    },
    MuiStyledDrawer: {
      styleOverrides: {
        root: {
          position: "fixed",
          right: 0,
          top: 0,
          height: "100%",
          backgroundColor: "#FAF8FF",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
          overflowY: "auto",
          zIndex: 1200,
          transition: "transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms",
        },
      },
    },
    MuiSlidingSidebar: {
      styleOverrides: {
        root: {
          position: "fixed",
          right: 0,
          top: 0,
          height: "100%",
          backgroundColor: "#FAF8FF",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
          overflowY: "auto",
          zIndex: 1200,
          transition: "transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms",
        },
      },
    },
    MuiSidebarEditForm: {
      styleOverrides: {
        root: { padding: '16px' },
        section: {
          padding: '16px',
          marginBottom: '16px',
          backgroundColor: '#FAF8FF',
        },
        field: {
          marginBottom: '16px',
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
          },
          '& .MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
            backgroundColor: '#FAF8FF',
            padding: '0 8px',
          },
          '& .MuiInputLabel-outlined': {
            color: 'rgba(0, 0, 0, 0.87)',
          },
          '& .MuiInputLabel-outlined.Mui-focused': {
            color: '#6B20F5',
          },
        },
      },
    },
  },
});

export default theme;
